import { animate, motion, useReducedMotion } from "framer-motion";
import { useEffect, useState } from "react";
import { useT } from "~/core/i18n";

/**
 * The five demonstrations the sign-in panel plays beside the form.
 *
 * Each one shows the mechanism of a stage rather than a claim about it: what a
 * reading is counted over, what a draft is allowed to say, what publication
 * leaves behind. A landing page has four seconds and a claim takes all four to
 * be disbelieved; a mechanism is checkable while it is being read.
 *
 * Nothing here is a customer count, a logo wall or a rating. The product has
 * not launched, so the only honest trust signal is how the thing works — and a
 * fabricated one is worth less than none on the screen where somebody decides
 * whether to hand over an address.
 *
 * `play` is false when the viewer asked for less motion: every demo then draws
 * its finished state on the first frame instead of arriving at it.
 */
export interface DemoProps {
	play: boolean;
}

/** Count from zero to `to` once, or start finished when motion is off. */
function useCount(to: number, play: boolean) {
	const [value, setValue] = useState(play ? 0 : to);
	useEffect(() => {
		if (!play) return setValue(to);
		const controls = animate(0, to, {
			duration: 1.1,
			ease: "easeOut",
			onUpdate: (next) => setValue(Math.round(next)),
		});
		return () => controls.stop();
	}, [to, play]);
	return value;
}

/** Fade-and-rise, staggered by row index. Identity at rest when motion is off. */
function rise(play: boolean, index: number) {
	if (!play) return {};
	return {
		initial: { opacity: 0, y: 6 },
		animate: { opacity: 1, y: 0 },
		transition: { delay: 0.15 + index * 0.18, duration: 0.4, ease: "easeOut" as const },
	};
}

/**
 * Detect — one question, three engines, one figure.
 *
 * The ranks are what a visibility reading is made of, and the note under the
 * figure is the definition the metric actually uses. Naming the denominator on
 * the marketing surface is the cheapest promise to keep: it is the same
 * sentence the reading screen prints.
 */
export function DetectDemo({ play }: DemoProps) {
	const t = useT();
	const percent = useCount(62, play);
	const rows = [
		{ engine: "chatgpt", rank: "#2" },
		{ engine: "doubao", rank: null },
	];
	return (
		<div className="demo">
			<p className="demo-ask">{t("auth.demo.ask")}</p>
			<ul className="demo-rows">
				{rows.map((row, index) => (
					<motion.li key={row.engine} {...rise(play, index)}>
						<span>{t(`engine.${row.engine}`)}</span>
						{row.rank ? (
							<span className="demo-rank">{row.rank}</span>
						) : (
							<span className="demo-rank is-absent">{t("auth.demo.absent")}</span>
						)}
					</motion.li>
				))}
			</ul>
			<div className="demo-figure">
				<span className="demo-num">{percent}%</span>
				<span className="demo-caption">{t("auth.demo.visibility")}</span>
			</div>
		</div>
	);
}

/**
 * Analyse — the same answers, split between everybody who was named.
 *
 * The bars are share of voice, which is the brand's namings over every brand's.
 * Drawing the competitors beside it is the point: a figure with no denominator
 * on screen is a figure nobody can argue with, and those are the ones that get
 * quietly disbelieved.
 */
export function AnalyseDemo({ play }: DemoProps) {
	const t = useT();
	const bars = [
		{ name: "auth.demo.you", share: 31, mine: true },
		{ name: "auth.demo.rivalA", share: 24, mine: false },
		{ name: "auth.demo.rest", share: 45, mine: false },
	];
	return (
		<div className="demo">
			<ul className="demo-bars">
				{bars.map((bar, index) => (
					<li key={bar.name} data-mine={bar.mine || undefined}>
						<span className="demo-bar-name">{t(bar.name)}</span>
						<span className="demo-bar-track">
							<motion.span
								className="demo-bar-fill"
								initial={play ? { width: 0 } : false}
								animate={{ width: `${bar.share}%` }}
								transition={{ delay: 0.15 + index * 0.12, duration: 0.6, ease: "easeOut" }}
							/>
						</span>
						<span className="demo-bar-value">{bar.share}%</span>
					</li>
				))}
			</ul>
		</div>
	);
}

/**
 * Generate — three sentences with a source, and one without.
 *
 * The fourth line is the demonstration: the check pass strikes what went past
 * the fact table, before anyone reads it. That is the part a customer cannot
 * verify from the outside and the part they most need to believe, so it is
 * shown happening rather than asserted in a bullet.
 */
export function GenerateDemo({ play }: DemoProps) {
	const t = useT();
	const lines = [
		{ key: "auth.demo.line1", source: "auth.demo.source1" },
		{ key: "auth.demo.line2", source: "auth.demo.source2" },
	];
	return (
		<div className="demo">
			<ul className="demo-lines">
				{lines.map((line, index) => (
					<motion.li key={line.key} {...rise(play, index)}>
						<span>{t(line.key)}</span>
						<span className="demo-source">{t(line.source)}</span>
					</motion.li>
				))}
				<motion.li
					className="demo-struck"
					initial={play ? { opacity: 0 } : false}
					animate={play ? { opacity: [0, 1, 1, 0.45] } : { opacity: 0.45 }}
					transition={{ delay: 0.7, duration: 1.4, times: [0, 0.2, 0.6, 1] }}
				>
					<span>{t("auth.demo.line4")}</span>
					<span className="demo-source is-cut">{t("auth.demo.cut")}</span>
				</motion.li>
			</ul>
		</div>
	);
}

/**
 * Publish — the same blocks on two domains, each citing the other.
 *
 * A listing is worth citing because the brand does not own it, and the brand's
 * own site has to answer the same questions. The line between the two cards is
 * the citation that keeps the pair from reading as duplicated copy.
 */
export function PublishDemo({ play }: DemoProps) {
	const t = useT();
	return (
		<div className="demo">
			<div className="demo-publish">
				<motion.div className="demo-card" {...rise(play, 0)}>
					<span className="demo-card-host">ondit.ai</span>
					<span className="demo-card-title">{t("auth.demo.listing")}</span>
					<span className="demo-chip">FAQPage</span>
				</motion.div>
				<div className="demo-link">
					<span className="demo-link-label">{t("auth.demo.cites")}</span>
					<motion.span
						className="demo-link-rule"
						initial={play ? { scaleX: 0 } : false}
						animate={{ scaleX: 1 }}
						transition={{ delay: 0.5, duration: 0.45, ease: "easeOut" }}
					>
						{play ? (
							<motion.span
								className="demo-link-mote"
								animate={{ left: ["0%", "100%", "0%"] }}
								transition={{ delay: 1, duration: 4.4, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" }}
							/>
						) : null}
					</motion.span>
				</div>
				<motion.div className="demo-card" {...rise(play, 2)}>
					<span className="demo-card-host">{t("auth.demo.yourSite")}</span>
					<span className="demo-card-title">{t("auth.demo.embed")}</span>
					<span className="demo-chip">{t("auth.demo.sameBlocks")}</span>
				</motion.div>
			</div>
		</div>
	);
}

/**
 * Reflow — what publication changed, back at the top of the loop.
 *
 * The curve is citations over the weeks after a page went out, which is the
 * only evidence that any of the four stages before it were worth running.
 */
export function ReflowDemo({ play }: DemoProps) {
	const t = useT();
	const citations = useCount(37, play);
	return (
		<div className="demo demo-reflow">
			<svg viewBox="0 0 400 64" className="demo-curve" aria-hidden>
				<motion.path
					d="M4 56 L84 50 L164 38 L244 32 L324 16 L396 6"
					fill="none"
					stroke="currentColor"
					strokeWidth="2"
					strokeLinecap="round"
					strokeLinejoin="round"
					initial={play ? { pathLength: 0 } : false}
					animate={{ pathLength: 1 }}
					transition={{ delay: 0.2, duration: 1.1, ease: "easeOut" }}
				/>
				<motion.circle
					cx="396"
					cy="6"
					r="3.5"
					fill="currentColor"
					style={{ transformBox: "fill-box", transformOrigin: "center" }}
					initial={play ? { scale: 0, opacity: 0 } : false}
					animate={play ? { scale: [1, 1.7, 1], opacity: [1, 0.3, 1] } : { opacity: 1 }}
					transition={
						play ? { delay: 1.2, duration: 2.4, repeat: Number.POSITIVE_INFINITY, ease: "easeInOut" } : undefined
					}
				/>
			</svg>
			<div className="demo-figure">
				<span className="demo-num">+{citations}</span>
				<span className="demo-caption">{t("auth.demo.citations")}</span>
			</div>
		</div>
	);
}

/** The demonstration each stage of the loop plays, keyed as `LOOP` keys it. */
export const DEMOS: Record<string, (props: DemoProps) => React.ReactElement> = {
	detect: DetectDemo,
	analyse: AnalyseDemo,
	generate: GenerateDemo,
	publish: PublishDemo,
	reflow: ReflowDemo,
};

/** True when the viewer asked their system for less motion. */
export function usePlay() {
	return !useReducedMotion();
}
